import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gauge as GaugeIcon, Activity, MapPin, Wrench, ArrowRight, ShieldCheck, Timer, Globe2 } from "lucide-react";
import { api } from "../lib/api";
import { trackClick } from "../lib/analytics";
import { NewsFeed } from "../components/NewsFeed";
import { AdSlot } from "../components/AdSlot";
import { AD_SLOTS } from "../lib/adSlots";

const tools = [
  {
    to: "/speed-test",
    icon: GaugeIcon,
    title: "Speed Test",
    desc: "Ping, download and upload — with a plain-English verdict.",
    accent: "from-[#4A78FF] to-[#2D5AFF]",
    testid: "landing-card-speed-test",
  },
  {
    to: "/down-detector",
    icon: Activity,
    title: "Down Detector",
    desc: "See which major services are having a bad day right now.",
    accent: "from-[#5C85FF] to-[#3D6BFF]",
    testid: "landing-card-down-detector",
  },
  {
    to: "/my-ip",
    icon: MapPin,
    title: "My IP & Location",
    desc: "Your public IP, ISP and approximate location — one click to copy.",
    accent: "from-[#6D93FF] to-[#4A78FF]",
    testid: "landing-card-my-ip",
  },
  {
    to: "/fix-my-internet",
    icon: Wrench,
    title: "Fix My Internet",
    desc: "A step-by-step checklist when things aren't working.",
    accent: "from-[#3D6BFF] to-[#7AA4FF]",
    testid: "landing-card-fix-my-internet",
  },
];

export default function Landing() {
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    api.get("/geo").then((r) => setGeo(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 pt-10 sm:pt-16 lg:pt-24 pb-10 sm:pb-12 lg:pb-20">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[rgba(61,107,255,0.3)] bg-[rgba(61,107,255,0.08)] text-xs font-mono uppercase tracking-wider text-[var(--cobalt-bright)] mb-5">
              <span className="dot dot-up pulse" /> Live · No sign-up required
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Is it <span className="text-[var(--red)]">you</span>, or is it{" "}
              <span className="relative inline-block">
                <span className="text-[var(--cobalt-bright)]">the internet?</span>
                <span className="absolute -bottom-1 left-0 right-0 h-1 rounded-full" style={{ background: "linear-gradient(90deg, transparent, #3D6BFF, transparent)" }} />
              </span>
            </h1>
            <p className="mt-5 sm:mt-6 text-sm sm:text-base lg:text-lg text-[var(--text-dim)] max-w-xl leading-relaxed">
              Three tools, one page. Run a real speed test, see which services are down,
              and find your public IP — then get a plain-English answer, not a spreadsheet.
            </p>

            <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/speed-test" onClick={() => trackClick("hero-cta-speed-test")} className="btn-cobalt inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium" data-testid="hero-cta-speed-test">
                <GaugeIcon className="w-5 h-5" /> Start speed test
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/down-detector" onClick={() => trackClick("hero-cta-down-detector")} className="btn-ghost inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium" data-testid="hero-cta-down-detector">
                <Activity className="w-5 h-5" /> Check service status
              </Link>
            </div>

            <div className="mt-7 sm:mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[var(--text-muted)]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--cobalt-bright)]" /> No tracking</div>
              <div className="flex items-center gap-2"><Timer className="w-4 h-4 text-[var(--cobalt-bright)]" /> Under 20 seconds</div>
              <div className="flex items-center gap-2"><Globe2 className="w-4 h-4 text-[var(--cobalt-bright)]" /> Global test servers</div>
            </div>
          </div>

          {/* Live IP card */}
          <div className="lg:col-span-5 min-w-0">
            <div className="glass-strong rounded-2xl p-5 sm:p-6 fade-up" style={{ animationDelay: "0.1s" }}>
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-muted)]">Your connection</div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="dot dot-up pulse" /> <span className="text-[var(--text-dim)]">online</span>
                </div>
              </div>
              <div className="space-y-4">
                <Row label="Public IP" value={geo?.ip || "…"} mono testid="landing-geo-ip" />
                <Row label="ISP" value={geo?.isp || "…"} testid="landing-geo-isp" />
                <Row label="Location" value={geo ? [geo.city, geo.region, geo.country].filter(Boolean).join(", ") : "…"} testid="landing-geo-location" />
                <Row label="Timezone" value={geo?.timezone || "…"} mono testid="landing-geo-tz" />
              </div>
              <Link to="/my-ip" className="mt-5 inline-flex items-center gap-2 text-sm text-[var(--cobalt-bright)] hover:text-white transition" data-testid="landing-geo-more">
                See full details <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Tools grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 pb-16 sm:pb-24">
        <div className="mb-6 sm:mb-8 flex items-end justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-2">/ The toolkit</div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold">Four tools, zero friction</h2>
          </div>
        </div>

        {/* Ad: between hero and tools grid */}
        <div className="mb-8">
          <AdSlot slot={AD_SLOTS.landingMid} name="landing-mid" minHeight={100} />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {tools.map((t, i) => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className="glass rounded-2xl p-6 lift group fade-up block"
              style={{ animationDelay: `${0.05 * i}s` }}
            >
              <div className={`w-11 h-11 rounded-xl mb-4 flex items-center justify-center bg-gradient-to-br ${t.accent}`} style={{ boxShadow: "0 0 32px -8px rgba(61,107,255,0.5)" }}>
                <t.icon className="w-5 h-5 text-white" strokeWidth={2.2} />
              </div>
              <h3 className="font-display text-lg font-semibold mb-1">{t.title}</h3>
              <p className="text-sm text-[var(--text-dim)] leading-relaxed mb-5">{t.desc}</p>
              <div className="text-sm text-[var(--cobalt-bright)] flex items-center gap-1.5 group-hover:gap-3 transition-all">
                Open <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Live news & tips feed */}
      <section className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 pb-16 sm:pb-24">
        <div className="mb-5 sm:mb-6">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-2">/ Pulse</div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold">What's happening online</h2>
          <p className="text-sm text-[var(--text-dim)] mt-2">Fresh outage reports, community chatter and quick tips to speed up your connection.</p>
        </div>
        <NewsFeed testid="landing-news-feed" />

        {/* Ad: below news feed */}
        <div className="mt-6">
          <AdSlot slot={AD_SLOTS.landingFeed} name="landing-feed" minHeight={120} />
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, mono, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 min-w-0">
      <span className="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)] shrink-0">{label}</span>
      <span className={`text-right text-sm truncate ${mono ? "font-mono" : ""} text-white`} data-testid={testid} title={value}>{value}</span>
    </div>
  );
}
